import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, Copy, CreditCard, Loader2, Lock, Scissors, Sparkles } from 'lucide-react';
import api from '../utils/api';

const planLabels = {
  basico: 'Basico',
  pro: 'Pro',
  premium: 'Premium'
};

const planFeatures = {
  basico: ['Pagina publica de agendamento', 'WhatsApp automatico', 'Dashboard essencial'],
  pro: ['Agendamentos ilimitados', 'Multiplos barbeiros', 'Relatorios e faturamento'],
  premium: ['Tudo do Pro', 'Recursos avancados', 'Prioridade em melhorias']
};

const planComparison = [
  ['Pagina publica por slug', true, true, true],
  ['WhatsApp automatico', true, true, true],
  ['Multiplos barbeiros', false, true, true],
  ['Relatorios e faturamento', false, true, true],
  ['Recursos premium e prioridade', false, false, true]
];

function generateSlugPreview(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default function SaasCheckoutPage({ mode = 'new' }) {
  const { plan = 'pro', barbershopId } = useParams();
  const navigate = useNavigate();
  const normalizedPlan = ['basico', 'pro', 'premium'].includes(plan) ? plan : 'pro';
  const isRenewal = mode === 'renewal';

  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [payment, setPayment] = useState(null);
  const [pix, setPix] = useState(null);
  const [approved, setApproved] = useState(false);
  const [signup, setSignup] = useState({ nome: '', telefone: '', slug: '', username: '', senha: '' });
  const [nameSuggestions, setNameSuggestions] = useState([]);

  const title = useMemo(() => (
    isRenewal ? 'Renove seu BarberFlow' : `Plano ${planLabels[normalizedPlan]} BarberFlow`
  ), [isRenewal, normalizedPlan]);

  const createCheckout = async () => {
    setLoading(true);
    setError('');
    setNameSuggestions([]);
    try {
      const response = await api.post('/billing/checkout', {
        plan: normalizedPlan,
        type: isRenewal ? 'renewal' : 'new_subscription',
        barbershopId: isRenewal ? barbershopId : undefined
      });
      setPayment(response.data.payment);
      setPix(response.data.pix);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao gerar Pix');
    } finally {
      setLoading(false);
    }
  };

  const checkPayment = useCallback(async () => {
    if (!payment?.id || approved) return;
    setChecking(true);
    try {
      const response = await api.get(`/billing/payments/${payment.id}/status`);
      const nextPayment = response.data.payment;
      setPayment(nextPayment);
      if (nextPayment.status === 'approved') {
        setApproved(true);
        if (isRenewal) {
          setTimeout(() => navigate(`/dashboard/${nextPayment.barbershopId}`), 1200);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao consultar pagamento');
    } finally {
      setChecking(false);
    }
  }, [payment?.id, approved, isRenewal, navigate]);

  useEffect(() => {
    createCheckout();
  }, []);

  useEffect(() => {
    if (!payment?.id || approved) return undefined;
    const interval = setInterval(checkPayment, 5000);
    return () => clearInterval(interval);
  }, [payment?.id, approved, checkPayment]);

  const copyPix = async () => {
    if (!pix?.qr_code) return;
    await navigator.clipboard.writeText(pix.qr_code);
  };

  const completeSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        nome: signup.nome,
        telefone: signup.telefone,
        username: signup.username,
        senha: signup.senha
      };
      const response = await api.post(`/billing/payments/${payment.id}/complete-signup`, payload);
      const barbershop = response.data.barbershop;
      navigate(`/login?slug=${barbershop.username || signup.username || barbershop.slug || barbershop.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar barbearia');
      setNameSuggestions(err.response?.data?.suggestions || []);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 text-white px-4 py-10">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_420px] gap-8 items-start">
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-600 to-purple-600 flex items-center justify-center">
              <Scissors className="w-5 h-5" />
            </div>
            <div>
              <p className="text-primary-300 text-sm">BarberFlow</p>
              <h1 className="text-3xl font-bold">{title}</h1>
            </div>
          </div>

          <div className="card p-6 space-y-5">
            {normalizedPlan === 'pro' && (
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-500/30 bg-primary-500/10 px-3 py-1 text-sm text-primary-200">
                <Sparkles className="w-4 h-4" />
                Plano mais escolhido
              </div>
            )}
            <div className="flex items-center gap-2 text-primary-300">
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">O que voce libera agora</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {(planFeatures[normalizedPlan] || planFeatures.pro).map(feature => (
                <div key={feature} className="rounded-lg border border-dark-700 bg-dark-800 p-4">
                  <CheckCircle className="w-5 h-5 text-green-400 mb-3" />
                  <p className="text-sm text-gray-200">{feature}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400">
              O plano e mensal, com 7 dias de teste gratis para novas barbearias. Depois do pagamento aprovado, o acesso e liberado automaticamente.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Comparacao dos planos</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-400">
                  <tr>
                    <th className="text-left py-3">Recurso</th>
                    <th className="py-3">Basico</th>
                    <th className="py-3 text-primary-300">Pro</th>
                    <th className="py-3">Premium</th>
                  </tr>
                </thead>
                <tbody>
                  {planComparison.map(([feature, basico, pro, premium]) => (
                    <tr key={feature} className="border-t border-dark-700">
                      <td className="py-3 text-gray-200">{feature}</td>
                      {[basico, pro, premium].map((enabled, index) => (
                        <td key={index} className="py-3 text-center">
                          {enabled ? <CheckCircle className="w-4 h-4 text-green-400 mx-auto" /> : <span className="text-gray-600">-</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4">Garantia operacional: se o Pix aprovar, o sistema libera o painel automaticamente.</p>
          </div>

          {approved && !isRenewal && (
            <form onSubmit={completeSignup} className="card p-6 space-y-4">
              <div>
                <h2 className="text-xl font-semibold">Pagamento aprovado</h2>
                <p className="text-gray-400 text-sm mt-1">Agora crie sua barbearia para liberar o dashboard.</p>
              </div>
              {nameSuggestions.length > 0 && (
                <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3 text-sm text-yellow-100">
                  <p className="mb-2">Esse nome ja esta em uso. Escolha uma sugestao:</p>
                  <div className="flex flex-wrap gap-2">
                    {nameSuggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => {
                          setSignup(prev => ({
                            ...prev,
                            nome: suggestion,
                            slug: generateSlugPreview(suggestion)
                          }));
                          setNameSuggestions([]);
                          setError('');
                        }}
                        className="px-3 py-1 rounded-lg border border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <input
                className="input"
                required
                placeholder="Nome publico da barbearia"
                value={signup.nome}
                onChange={(e) => {
                  const nome = e.target.value;
                  setSignup(prev => ({
                    ...prev,
                    nome,
                    slug: generateSlugPreview(nome)
                  }));
                }}
              />
              <p className="-mt-2 text-xs text-gray-500">Esse nome aparece no dashboard e na pagina publica.</p>
              <input className="input" required placeholder="WhatsApp do dono" value={signup.telefone} onChange={(e) => setSignup({ ...signup, telefone: e.target.value })} />
              <input
                className="input"
                required
                placeholder="Usuario para entrar no dashboard, ex: dono-imperio"
                value={signup.username}
                onChange={(e) => setSignup({ ...signup, username: generateSlugPreview(e.target.value) })}
              />
              <p className="-mt-2 text-xs text-gray-500">Esse e o nome que voce usa no login do painel.</p>
              <input className="input" required type="password" placeholder="Senha de acesso" value={signup.senha} onChange={(e) => setSignup({ ...signup, senha: e.target.value })} />
              <button className="btn-primary w-full" disabled={loading}>
                {loading ? 'Criando...' : 'Criar barbearia e acessar'}
              </button>
            </form>
          )}
        </section>

        <aside className="card p-6 sticky top-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-sm text-gray-400">Checkout Pix</p>
              <p className="text-2xl font-bold">R$ {Number(payment?.amount || 0).toFixed(2)}</p>
            </div>
            <CreditCard className="w-8 h-8 text-primary-400" />
          </div>

          {error && <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}

          {loading && !payment && (
            <div className="flex items-center justify-center py-14 text-gray-300">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Gerando Pix...
            </div>
          )}

          {payment && (
            <div className="space-y-4">
              {pix?.qr_code_base64 && !approved && (
                <div className="bg-white rounded-lg p-4">
                  <img className="w-full aspect-square object-contain" src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code Pix" />
                </div>
              )}

              {approved ? (
                <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-green-300">
                  <CheckCircle className="w-6 h-6 mb-2" />
                  Pagamento aprovado.
                </div>
              ) : (
                <>
                  <button onClick={copyPix} className="btn-secondary w-full">
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar codigo Pix
                  </button>
                  <button onClick={checkPayment} disabled={checking} className="btn-primary w-full">
                    {checking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Ja paguei
                  </button>
                  <p className="text-xs text-gray-500 flex items-center gap-2">
                    <Lock className="w-3 h-3" />
                    Polling automatico a cada 5 segundos. O Pix expira em 15 minutos.
                  </p>
                </>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
